import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useTranslations } from "use-intl";

import { billingCardQueryOptions } from "../billing.queries";
import type { BillingAddress } from "../billing.types";
import { PaymentCard } from "./payment-card";

// Non-empty address lines, postal+city and state+country combined.
function addressLines(a: BillingAddress): string[] {
	return [
		a.line1,
		a.line2,
		[a.postalCode, a.city].filter(Boolean).join(" "),
		[a.state, a.country].filter(Boolean).join(", "),
	].filter((l): l is string => Boolean(l?.trim()));
}

function Field({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div className="flex flex-col gap-0.5">
			<dt className="text-muted-foreground text-xs tracking-wide uppercase">{label}</dt>
			<dd className="font-medium">{children}</dd>
		</div>
	);
}

export function BillingInfo() {
	const t = useTranslations("billing.info");
	const { data } = useQuery(billingCardQueryOptions());
	const card = data?.card;
	const billing = data?.billing;
	const lines = billing?.address ? addressLines(billing.address) : [];

	return (
		<div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
			<dl className="flex flex-col gap-4">
				{billing?.name && <Field label={t("billedTo")}>{billing.name}</Field>}
				{billing?.email && <Field label={t("email")}>{billing.email}</Field>}
				{lines.length > 0 && (
					<Field label={t("address")}>
						{lines.map((l) => (
							<span key={l} className="block font-normal">
								{l}
							</span>
						))}
					</Field>
				)}
			</dl>

			{card && <PaymentCard card={card} />}
		</div>
	);
}
